import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import type {
  InviteLookupRow,
  InviteValidateResponse,
  ActiveProductOption,
  ActiveModelOption,
} from '../types/api'
import type { Property, Company } from '../types/application'
import { throwServiceError, unwrapArrayPayload } from './serviceHelpers'

/** Search properties — GET /api/all_property with optional q and company_id. */
export async function getProperties(
  searchText: string = '',
  companyId?: string | number,
): Promise<Property[]> {
  try {
    const params = new URLSearchParams()
    if (searchText) params.append('q', searchText)
    if (companyId) params.append('company_id', String(companyId))
    params.append('listed', '1')
    params.append('page', 'show_listed')

    const response = await apiClient.get<Property[]>(
      `${API_ENDPOINTS.ALL_PROPERTY}?${params.toString()}`,
    )

    return unwrapArrayPayload<Property>(response.data)
  } catch (error) {
    throwServiceError(error, 'Failed to fetch properties.')
  }
}

/** Advance property search — GET /api/all_property with state/city (Angular AdvanceSearchComponent). */
export async function getPropertiesByStateCity(params: {
  state?: string
  city?: string
}): Promise<Property[]> {
  try {
    const response = await apiClient.get<Property[] | unknown>(API_ENDPOINTS.ALL_PROPERTY, {
      params: {
        listed: '1',
        ...(params.state ? { state: params.state } : {}),
        ...(params.city ? { city: params.city } : {}),
      },
    })

    return unwrapArrayPayload<Property>((response as { data?: unknown })?.data)
  } catch (error) {
    throwServiceError(error, 'Failed to fetch properties.')
  }
}

/** City map by state — GET /city (Angular AdvanceSearchComponent.getCity). */
export async function getCityMap(): Promise<Record<string, string[]>> {
  try {
    const response = await apiClient.get<Record<string, string[]>>('/city')
    return response.data && typeof response.data === 'object' ? response.data : {}
  } catch (error) {
    throwServiceError(error, 'Failed to fetch cities.')
  }
}

/** Search companies — GET /api/all_companies with optional q. */
export async function getCompanies(searchText: string = ''): Promise<Company[]> {
  try {
    const params = new URLSearchParams()
    if (searchText) params.append('q', searchText)
    params.append('listed', '1')
    params.append('page', 'show_listed')

    const response = await apiClient.get<Company[]>(
      `${API_ENDPOINTS.ALL_COMPANIES}?${params.toString()}`,
    )

    return unwrapArrayPayload<Company>(response.data)
  } catch (error) {
    throwServiceError(error, 'Failed to fetch companies.')
  }
}

/** Look up invites by email/mobile — GET /look_up (Angular lookupInvite). */
export async function lookupInvite(params: {
  email?: string
  mobile_no?: string
}): Promise<InviteLookupRow[]> {
  try {
    const query: Record<string, string> = {}
    if (params.email) query.email = params.email
    if (params.mobile_no) query.mobile_no = params.mobile_no
    const response = await apiClient.get<InviteLookupRow[] | unknown>('/look_up', {
      params: query,
    })
    const data = response.data
    return Array.isArray(data) ? (data as InviteLookupRow[]) : []
  } catch (error) {
    throwServiceError(error, 'Failed to look up invites.')
  }
}

/** Validate invite code — GET /invite/{code} (Angular validateInvite). */
export async function validateInvite(
  code: string,
): Promise<InviteValidateResponse | null> {
  try {
    const response = await apiClient.get<InviteValidateResponse | unknown>(
      `/invite/${encodeURIComponent(code)}`,
    )
    const data = (response as { data?: unknown })?.data
    return data && typeof data === 'object'
      ? (data as InviteValidateResponse)
      : null
  } catch (error) {
    throwServiceError(error, 'Failed to validate invite code.')
  }
}

export async function getActiveProductOptions(): Promise<ActiveProductOption[]> {
  try {
    const response = await apiClient.get<{ data?: ActiveProductOption[] }>(
      '/api/product/active/list',
      { params: { perPage: 500 } },
    )
    const rows = response.data?.data
    return Array.isArray(rows) ? rows : []
  } catch (error) {
    throwServiceError(error, 'Failed to load products.')
  }
}

export async function getActiveModelOptions(): Promise<ActiveModelOption[]> {
  try {
    const response = await apiClient.get<{ data?: ActiveModelOption[] }>(
      '/api/model/active/list',
      { params: { perPage: 500 } },
    )
    const rows = response.data?.data
    return Array.isArray(rows) ? rows : []
  } catch (error) {
    throwServiceError(error, 'Failed to load models.')
  }
}

export async function getApplicationRiskRatingLabels(): Promise<string[]> {
  try {
    const response = await apiClient.get<{ labels?: unknown }>(
      '/api/application/risk_rating_labels',
    )
    const raw = response.data?.labels
    const labels = Array.isArray(raw) ? raw : []
    const out: string[] = []
    for (const label of labels) {
      if (label == null) continue
      const t = String(label).trim()
      if (!t || ['null', 'undefined'].includes(t.toLowerCase())) continue
      out.push(t)
    }
    return out
  } catch (error) {
    throwServiceError(error, 'Failed to load risk rating labels.')
  }
}
